import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { globalEmployees, Employee as FullEmployee } from '../utils/mockEmployeeData';
import { Employee } from '../types';
import { RootState } from '../store';
import { 
  Colors, 
  Spacing, 
  FontSizes, 
  FontWeights, 
  BorderRadius, 
  Shadows,
  FontStacks 
} from '../constants/theme';
import UnifiedPageHeader from '../components/UnifiedPageHeader';
import PageContainer from '../components/layout/PageContainer';
import ModernTable, { Column, CellHelpers } from '../components/tables/ModernTable';
import { filterEmployees } from '../utils/roleBasedAccess';

// Content wrapper for sections within the page 
const ContentWrapper = styled.div`
  padding-bottom: ${Spacing.xl}px;
`;

const SectionCard = styled.div`
  background: ${Colors.surface};
  border: 1px solid ${Colors.borderLight};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.sm};
  margin-bottom: ${Spacing.xl}px;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: ${Spacing.lg}px ${Spacing.xl}px;
  border-bottom: 1px solid ${Colors.border};
  background: linear-gradient(to right, ${Colors.background}, ${Colors.backgroundDark});
`;

const SectionTitle = styled.h2`
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0;
`;

const TableSection = styled.div`
  padding: ${Spacing.lg}px;
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${Colors.border};
  margin-bottom: ${Spacing.lg}px;
`;

const Tab = styled.button<{ active?: boolean }>`
  padding: ${Spacing.md}px ${Spacing.lg}px;
  background: ${props => props.active ? Colors.primary : 'transparent'};
  color: ${props => props.active ? Colors.textOnPrimary : Colors.textSecondary};
  border: none;
  border-bottom: 2px solid ${props => props.active ? Colors.primary : 'transparent'};
  cursor: pointer;
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? Colors.primaryDark : Colors.surfaceHover};
    color: ${props => props.active ? Colors.textOnPrimary : Colors.textPrimary};
  }
`;

// Loading skeleton component
const LoadingTable = styled.div`
  padding: ${Spacing.lg}px;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
`;

const SkeletonRow = styled.div`
  height: 60px;
  background: linear-gradient(90deg, ${Colors.background} 25%, ${Colors.backgroundDark} 50%, ${Colors.background} 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: ${BorderRadius.sm}px;
  
  @keyframes loading {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

// Empty state component
const EmptyState = styled.div`
  padding: ${Spacing.xl * 2}px ${Spacing.xl}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.lg}px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  background: ${Colors.background};
  border-radius: ${BorderRadius.round}px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${FontSizes.xxl}px;
  color: ${Colors.textTertiary};
`;

const EmptyTitle = styled.h3`
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0;
`;

const EmptyMessage = styled.p`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  margin: 0;
  max-width: 400px;
`;

const EmptyActions = styled.div`
  display: flex;
  gap: ${Spacing.md}px;
  flex-wrap: wrap;
  justify-content: center;
`;

const ActionButton = styled.button`
  padding: ${Spacing.sm}px ${Spacing.lg}px;
  background: ${Colors.primary};
  color: ${Colors.textOnPrimary};
  border: none;
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${Colors.primaryDark};
    transform: translateY(-1px);
  }
  
  &.secondary {
    background: ${Colors.surface};
    color: ${Colors.textPrimary};
    border: 1px solid ${Colors.border};
    
    &:hover {
      background: ${Colors.surfaceHover};
    }
  }
`;

// Error state component
const ErrorState = styled(EmptyState)`
  color: ${Colors.danger};
`;

const ErrorIcon = styled(EmptyIcon)`
  background: ${Colors.danger}10;
  color: ${Colors.danger};
`;

// Schema validator for employee data contract
const validateEmployeeData = (employee: FullEmployee): boolean => {
  const required = ['id', 'name', 'position', 'department', 'location', 'salary', 'currency'];
  const missing = required.filter(field => !employee[field as keyof FullEmployee]);
  
  if (missing.length > 0) {
    console.warn(`Employee ${employee.id} missing fields:`, missing);
    return false;
  }
  
  // Type checks
  if (isNaN(employee.performance) || isNaN(employee.engagement) || isNaN(employee.attritionRisk) || isNaN(employee.salary)) {
    console.warn(`Employee ${employee.id} has invalid numeric fields`);
    return false;
  }
  
  return true;
};

// Safe number conversion with fallbacks
const safeNumber = (value: any, fallback: number = 0): number => {
  const num = Number(value);
  return isNaN(num) ? fallback : num;
};

// Safe percentage scaling (0-1 to 0-100)
const safePercentage = (value: any): number => {
  const num = safeNumber(value, 0);
  // If value seems to be in 0-1 range, scale to 0-100
  return num <= 1 ? num * 100 : num;
};

// Convert full employee data to simplified Employee interface with validation
const convertToSimpleEmployee = (fullEmployee: FullEmployee): Employee | null => {
  if (!validateEmployeeData(fullEmployee)) {
    return null; // Filter out invalid records
  }

  return {
    id: fullEmployee.id,
    name: fullEmployee.name,
    position: fullEmployee.position,
    department: fullEmployee.department,
    location: fullEmployee.location,
    tenure: parseInt(fullEmployee.tenure.split(' ')[0]) || 0,
    roleLevel: fullEmployee.roleLevel,
    // Ensure percentages are in 0-100 range
    performanceScore: safePercentage(fullEmployee.performance),
    engagementScore: safePercentage(fullEmployee.engagement),
    attritionRisk: safePercentage(fullEmployee.attritionRisk),
    // Safe salary conversion with fallback
    salary: safeNumber(fullEmployee.salary, 0),
    currency: fullEmployee.currency || 'USD',
    leadershipPotential: fullEmployee.potentialRating === 'High' ? 85 : fullEmployee.potentialRating === 'Medium' ? 60 : 35,
    skills: fullEmployee.skills || [],
    manager: fullEmployee.manager,
    email: fullEmployee.email,
    hireDate: fullEmployee.hireDate.toISOString().split('T')[0],
    lastPromotion: fullEmployee.lastPromotion?.toISOString().split('T')[0],
    successionReadiness: fullEmployee.successorReadiness === 'Ready Now' ? 90 : 
                        fullEmployee.successorReadiness === '1-2 Years' ? 70 :
                        fullEmployee.successorReadiness === '2+ Years' ? 40 : 20
  };
};

// Use properly typed employee data with validation
const mockEmployees: Employee[] = globalEmployees
  .slice(0, 100)
  .map(convertToSimpleEmployee)
  .filter((employee): employee is Employee => employee !== null); // Filter out invalid records

const mockPerformanceData = [
  { id: '1', department: 'Medical Device Engineering', avgPerformance: 91.2, topPerformers: 425, needsImprovement: 89, trend: '+2.1%' },
  { id: '2', department: 'Clinical Operations', avgPerformance: 87.8, topPerformers: 2890, needsImprovement: 1245, trend: '+1.8%' },
  { id: '3', department: 'Data Analytics & AI', avgPerformance: 89.1, topPerformers: 178, needsImprovement: 45, trend: '+3.2%' },
  { id: '4', department: 'Regulatory Affairs', avgPerformance: 88.5, topPerformers: 95, needsImprovement: 12, trend: '+0.9%' },
  { id: '5', department: 'Quality Assurance', avgPerformance: 92.3, topPerformers: 142, needsImprovement: 28, trend: '+1.5%' },
  { id: '6', department: 'Software Development', avgPerformance: 90.8, topPerformers: 312, needsImprovement: 67, trend: '+2.7%' }
];

const mockAttritionData = [
  { id: '1', riskLevel: 'Very High (>80%)', count: 3, names: 'Jessica Wang, Tom Lee, Sarah Kim', actions: 'Immediate intervention' },
  { id: '2', riskLevel: 'High (60-80%)', count: 7, names: 'Mike Chen, Lisa Park, David Wu...', actions: 'Schedule 1:1s' },
  { id: '3', riskLevel: 'Medium (40-60%)', count: 12, names: 'Alex Smith, Emma Jones, John Doe...', actions: 'Monitor closely' },
  { id: '4', riskLevel: 'Low (20-40%)', count: 45, names: 'General employee population', actions: 'Regular check-ins' },
  { id: '5', riskLevel: 'Very Low (<20%)', count: 89, names: 'Stable employees', actions: 'Maintain engagement' }
];

interface EmployeeAnalyticsScreenProps {}

const EmployeeAnalyticsScreen: React.FC<EmployeeAnalyticsScreenProps> = () => {
  const { t } = useTranslation();
  const currentRole = useSelector((state: RootState) => state.user.currentRole);
  const [activeTab, setActiveTab] = useState<'employees' | 'performance' | 'attrition'>('employees');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter employees based on role with error handling
  const visibleEmployees = useMemo(() => {
    try {
      const filtered = filterEmployees(mockEmployees, currentRole);
      if (filtered.length === 0) {
        console.warn('No employees visible for current role:', currentRole);
      }
      return filtered;
    } catch (err) {
      console.error('Error filtering employees:', err);
      setError('Failed to load employee data');
      return [];
    }
  }, [currentRole]);

  // Simulate data loading
  React.useEffect(() => {
    setLoading(true);
    setError(null);
    
    // Simulate API call
    const timer = setTimeout(() => {
      try {
        // Data is already loaded, just validate
        if (mockEmployees.length === 0) {
          setError('No employee data available');
        }
        setLoading(false);
      } catch (err) {
        setError('Failed to load employee analytics');
        setLoading(false);
      }
    }, 800);
    
    return () => clearTimeout(timer);
  }, []);

  // Employee table columns
  const employeeColumns: Column[] = [
    {
      key: 'name',
      header: 'Employee',
      sortable: true,
      render: (value, row) => <CellHelpers.Avatar name={value} />
    },
    {
      key: 'position',
      header: 'Position',
      sortable: true,
    },
    {
      key: 'department',
      header: 'Department',
      sortable: true,
    },
    {
      key: 'roleLevel',
      header: 'Level',
      sortable: true,
      render: (value) => <CellHelpers.Badge value={value || 'N/A'} variant="info" />
    },
    {
      key: 'tenure',
      header: 'Tenure',
      sortable: true,
      align: 'center',
      render: (value) => {
        if (value == null) return <span style={{ color: Colors.textTertiary, fontSize: FontSizes.xs }}>—</span>;
        const years = Number(value);
        return (
          <span style={{ fontWeight: FontWeights.medium }}>
            {years} {years === 1 ? 'year' : 'years'}
          </span>
        );
      }
    },
    {
      key: 'performanceScore',
      header: 'Performance',
      sortable: true,
      align: 'center',
      render: (value) => {
        if (value == null || isNaN(value)) {
          return <span style={{ color: Colors.textTertiary, fontSize: FontSizes.xs }}>—</span>;
        }
        const numValue = Number(value);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <CellHelpers.Progress value={numValue} color={numValue >= 90 ? Colors.success : numValue >= 75 ? Colors.warning : Colors.danger} />
            <span style={{ fontSize: FontSizes.xs, color: Colors.textSecondary, textAlign: 'center' }}>{numValue.toFixed(1)}%</span>
          </div>
        );
      }
    },
    {
      key: 'engagementScore',
      header: 'Engagement',
      sortable: true,
      align: 'center',
      render: (value) => {
        if (value == null || isNaN(value)) {
          return <span style={{ color: Colors.textTertiary, fontSize: FontSizes.xs }}>—</span>;
        }
        const numValue = Number(value);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <CellHelpers.Progress value={numValue} color={Colors.info} />
            <span style={{ fontSize: FontSizes.xs, color: Colors.textSecondary, textAlign: 'center' }}>{numValue.toFixed(1)}%</span>
          </div>
        );
      }
    },
    {
      key: 'attritionRisk',
      header: 'Attrition Risk',
      sortable: true,
      align: 'center',
      render: (value) => {
        if (value == null || isNaN(value)) {
          return <CellHelpers.Badge value="N/A" variant="info" />;
        }
        const numValue = Number(value);
        const variant = numValue >= 20 ? 'danger' : numValue >= 10 ? 'warning' : 'success';
        return <CellHelpers.Badge value={`${numValue.toFixed(1)}%`} variant={variant} />;
      }
    },
    {
      key: 'salary',
      header: 'Salary',
      sortable: true,
      align: 'right',
      render: (value, row: any) => {
        if (value == null || isNaN(value) || value === 0) {
          return <span style={{ color: Colors.textTertiary, fontSize: FontSizes.xs }}>—</span>;
        }
        const currency = row?.currency || 'USD';
        return <CellHelpers.Currency value={Number(value)} currency={currency} />;
      }
    }
  ];

  // Performance table columns
  const performanceColumns: Column[] = [
    {
      key: 'department',
      header: 'Department',
      sortable: true,
    },
    {
      key: 'avgPerformance',
      header: 'Avg Performance',
      sortable: true,
      align: 'center',
      render: (value) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <CellHelpers.Progress value={value} color={value >= 90 ? Colors.success : Colors.warning} />
          <span style={{ fontSize: FontSizes.xs, color: Colors.textSecondary }}>{value.toFixed(1)}%</span>
        </div>
      )
    },
    {
      key: 'topPerformers',
      header: 'Top Performers',
      sortable: true,
      align: 'center',
      render: (value) => <CellHelpers.Badge value={value.toString()} variant="success" />
    },
    {
      key: 'needsImprovement',
      header: 'Needs Improvement',
      sortable: true,
      align: 'center',
      render: (value) => value > 0 ? <CellHelpers.Badge value={value.toString()} variant="warning" /> : <span style={{ color: Colors.textTertiary }}>None</span>
    },
    {
      key: 'trend',
      header: 'Trend',
      sortable: true,
      align: 'center',
      render: (value) => {
        const isPositive = value.startsWith('+');
        return (
          <span style={{ 
            color: isPositive ? Colors.success : Colors.danger,
            fontWeight: FontWeights.semibold 
          }}>
            {value}
          </span>
        );
      }
    }
  ];

  // Attrition table columns
  const attritionColumns: Column[] = [
    {
      key: 'riskLevel',
      header: 'Risk Level',
      sortable: true,
      render: (value) => {
        const variant = value.includes('Very High') ? 'danger' : 
                      value.includes('High') ? 'warning' : 
                      value.includes('Medium') ? 'info' : 'success';
        return <CellHelpers.Badge value={value} variant={variant} />;
      }
    },
    {
      key: 'count',
      header: 'Employee Count',
      sortable: true,
      align: 'center',
      render: (value) => (
        <span style={{ 
          fontSize: FontSizes.md, 
          fontWeight: FontWeights.semibold,
          color: Colors.textPrimary 
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'names',
      header: 'Employees',
      width: '40%'
    },
    {
      key: 'actions',
      header: 'Recommended Actions',
      width: '25%',
      render: (value) => (
        <span style={{ 
          fontStyle: 'italic',
          color: Colors.textSecondary 
        }}>
          {value}
        </span>
      )
    }
  ];

  const getCurrentData = (): any[] => {
    switch (activeTab) {
      case 'employees':
        return visibleEmployees;
      case 'performance':
        return mockPerformanceData;
      case 'attrition':
        return mockAttritionData;
      default:
        return [];
    }
  };

  const getCurrentColumns = () => {
    switch (activeTab) {
      case 'employees':
        return employeeColumns;
      case 'performance':
        return performanceColumns;
      case 'attrition':
        return attritionColumns;
      default:
        return [];
    }
  };

  return (
    <PageContainer>
      <UnifiedPageHeader
        title="Employee Analytics"
        subtitle="Comprehensive workforce analysis and insights"
        metadata={{
          timestamp: new Date(),
          count: visibleEmployees.length,
          status: 'Live'
        }}
        actions={[
          {
            label: 'Export Data',
            onClick: () => console.log('Export data'),
            variant: 'secondary'
          },
          {
            label: 'Generate Report',
            onClick: () => console.log('Generate report'),
            variant: 'primary'
          }
        ]}
      />

      <ContentWrapper>
        <SectionCard>
          <SectionHeader>
            <SectionTitle>Workforce Data Analysis</SectionTitle>
          </SectionHeader>
          
          <TabContainer>
            <Tab 
              active={activeTab === 'employees'} 
              onClick={() => setActiveTab('employees')}
            >
              Employee Directory ({visibleEmployees.length})
            </Tab>
            <Tab 
              active={activeTab === 'performance'} 
              onClick={() => setActiveTab('performance')}
            >
              Performance Analysis
            </Tab>
            <Tab 
              active={activeTab === 'attrition'} 
              onClick={() => setActiveTab('attrition')}
            >
              Attrition Risk Assessment
            </Tab>
          </TabContainer>

          <TableSection>
            {loading ? (
              <LoadingTable>
                {Array.from({ length: 8 }, (_, i) => (
                  <SkeletonRow key={i} />
                ))}
              </LoadingTable>
            ) : error ? (
              <ErrorState>
                <ErrorIcon>⚠️</ErrorIcon>
                <EmptyTitle>Can't load Employee Analytics</EmptyTitle>
                <EmptyMessage>{error}</EmptyMessage>
                <EmptyActions>
                  <ActionButton onClick={() => window.location.reload()}>
                    Retry
                  </ActionButton>
                  <ActionButton className="secondary" onClick={() => setError(null)}>
                    Dismiss
                  </ActionButton>
                </EmptyActions>
              </ErrorState>
            ) : getCurrentData().length === 0 ? (
              <EmptyState>
                <EmptyIcon>👥</EmptyIcon>
                <EmptyTitle>No {activeTab === 'employees' ? 'employees' : activeTab} data available</EmptyTitle>
                <EmptyMessage>
                  {activeTab === 'employees' 
                    ? "No employees match your current filters or you don't have access to view employee data in your current role."
                    : `No ${activeTab} data is currently available for your scope.`
                  }
                </EmptyMessage>
                <EmptyActions>
                  <ActionButton onClick={() => setCurrentPage(1)}>
                    Clear Filters
                  </ActionButton>
                  <ActionButton className="secondary" onClick={() => window.location.reload()}>
                    Refresh
                  </ActionButton>
                </EmptyActions>
              </EmptyState>
            ) : (
              <ModernTable
                columns={getCurrentColumns()}
                data={getCurrentData()}
                keyField="id"
                showPagination={true}
                pageSize={50}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                onRowClick={(row) => {
                  console.log('View profile:', row);
                  // TODO: Navigate to employee profile page
                  // navigate(`/employee/${row.id}`);
                }}
                emptyMessage={`No ${activeTab} data available`}
              />
            )}
          </TableSection>
        </SectionCard>
      </ContentWrapper>
    </PageContainer>
  );
};

export default EmployeeAnalyticsScreen;